import React from 'react';
import { Button, Empty, Input, Select, Segmented, Tooltip } from 'antd';
import { InfoCircleOutlined, SearchOutlined } from '@ant-design/icons';

import { Period, ViewRange } from './types';
import { intersects, kindColor } from './temporal';
import { percent, periodDates, readable } from './format';

const label = (value: string) => {
  const text = readable(value);
  return text.charAt(0).toLocaleUpperCase() + text.slice(1);
};

export type PeriodFilters = {
  query: string;
  kind: string;
  horizon: string;
  sort: string;
  scope?: string;
};
export const initialPeriodFilters: PeriodFilters = {
  query: '',
  kind: '',
  horizon: '',
  sort: 'importance',
  scope: 'all',
};

export const sessionCount = (period: Period) => {
  if (typeof period.trading_sessions === 'number')
    return period.trading_sessions;
  const observations =
    period.observation_count ?? period.metrics?.observation_count;
  return typeof observations === 'number'
    ? Math.max(0, observations - 1)
    : undefined;
};

export const filterPeriods = (
  periods: Period[],
  filters: PeriodFilters,
  viewport?: ViewRange | null,
) => {
  const query = filters.query.trim().toLocaleLowerCase();
  return periods
    .filter(
      period =>
        (filters.scope !== 'visible' || intersects(period, viewport || null)) &&
        (!filters.kind || period.kind === filters.kind) &&
        (!filters.horizon || period.horizon === filters.horizon) &&
        (!query ||
          `${period.title} ${period.summary} ${period.start_date} ${period.end_date} ${readable(period.kind)}`
            .toLocaleLowerCase()
            .includes(query)),
    )
    .sort((left, right) => {
      const importance =
        right.score - left.score ||
        left.start_date.localeCompare(right.start_date) ||
        left.id.localeCompare(right.id);
      switch (filters.sort) {
        case 'newest':
          return right.start_date.localeCompare(left.start_date) || importance;
        case 'oldest':
          return left.start_date.localeCompare(right.start_date) || importance;
        case 'magnitude':
          return (
            Math.abs(right.return_pct ?? 0) - Math.abs(left.return_pct ?? 0) ||
            importance
          );
        case 'longest':
          return (
            (sessionCount(right) ?? -1) - (sessionCount(left) ?? -1) ||
            importance
          );
        case 'shortest':
          return (
            (sessionCount(left) ?? Infinity) -
              (sessionCount(right) ?? Infinity) || importance
          );
        default:
          return importance;
      }
    });
};

type Props = {
  periods: Period[];
  filtered: Period[];
  selected: Period | null;
  filters: PeriodFilters;
  limit: number;
  editing: boolean;
  onFilters: (value: PeriodFilters) => void;
  onLimit: (value: number) => void;
  onSelect: (period: Period) => void;
  viewport?: ViewRange | null;
};

const PeriodList: React.FC<Props> = ({
  periods,
  filtered,
  selected,
  filters,
  limit,
  editing,
  onFilters,
  onLimit,
  onSelect,
  viewport,
}) => {
  const active = Boolean(
    filters.query ||
      filters.kind ||
      filters.horizon ||
      filters.sort !== 'importance' ||
      filters.scope === 'visible',
  );
  const kinds = Array.from(new Set(periods.map(period => period.kind))).sort();
  const horizons = ['days', 'weeks', 'months', 'years'].filter(horizon =>
    periods.some(period => period.horizon === horizon),
  );
  const change = (key: keyof PeriodFilters, value: string | undefined) =>
    onFilters({ ...filters, [key]: value || '' });
  return (
    <section
      className="market-explorer__periods"
      aria-label="Interesting periods"
    >
      <div className="market-explorer__section-heading">
        <h2>
          Interesting periods{' '}
          <span className="market-explorer__count">{periods.length}</span>
        </h2>
        <Tooltip title="Explore overlapping price patterns at different time scales. Numbered markers use the original importance rank, even when you filter or sort. Only a few markers are shown at once to keep the chart readable.">
          <button
            className="market-explorer__info"
            type="button"
            aria-label="About interesting periods"
          >
            <InfoCircleOutlined />
          </button>
        </Tooltip>
      </div>
      <p className="market-explorer__muted">
        Select a period to zoom in and explore its news.
      </p>
      {periods.length > 0 && (
        <div className="market-explorer__period-filters">
          <Segmented
            block
            size="small"
            aria-label="Period search scope"
            value={filters.scope || 'all'}
            options={[
              { label: 'Entire analysis', value: 'all' },
              { label: 'Visible window', value: 'visible' },
            ]}
            onChange={value => change('scope', String(value))}
          />
          {filters.scope === 'visible' && viewport && (
            <small className="market-explorer__muted">
              Intervals overlapping{' '}
              {periodDates(
                new Date(viewport.min).toISOString().slice(0, 10),
                new Date(viewport.max).toISOString().slice(0, 10),
              )}
            </small>
          )}
          <Input
            aria-label="Search periods"
            placeholder="Search periods or dates"
            prefix={<SearchOutlined />}
            allowClear
            value={filters.query}
            onChange={event => change('query', event.target.value)}
          />
          <div className="market-explorer__filter-pair">
            <Select
              aria-label="Filter period type"
              placeholder="All types"
              allowClear
              value={filters.kind || undefined}
              options={kinds.map(kind => ({
                value: kind,
                label: label(kind),
              }))}
              onChange={value => change('kind', value)}
            />
            <Select
              aria-label="Filter time scale"
              placeholder="All time scales"
              allowClear
              value={filters.horizon || undefined}
              options={horizons.map(horizon => ({
                value: horizon,
                label: label(horizon),
              }))}
              onChange={value => change('horizon', value)}
            />
          </div>
          <label className="market-explorer__period-sort">
            <span>Sort by</span>
            <Select
              aria-label="Sort periods"
              value={filters.sort}
              onChange={value => change('sort', value)}
              options={[
                { value: 'importance', label: 'Importance' },
                { value: 'newest', label: 'Newest first' },
                { value: 'oldest', label: 'Oldest first' },
                { value: 'magnitude', label: 'Largest move' },
                { value: 'longest', label: 'Longest first' },
                { value: 'shortest', label: 'Shortest first' },
              ]}
            />
          </label>
          {(active || periods.length > limit) && (
            <div className="market-explorer__filter-summary">
              <span>
                {filtered.length} matching · showing{' '}
                {Math.min(limit, filtered.length)}
              </span>
              {active && (
                <Button
                  type="link"
                  size="small"
                  onClick={() => onFilters(initialPeriodFilters)}
                >
                  Reset filters
                </Button>
              )}
            </div>
          )}
        </div>
      )}
      {editing && (
        <p className="market-explorer__edit-notice">
          Save or cancel your article edit before choosing another period.
        </p>
      )}
      {selected &&
        !filtered.slice(0, limit).some(period => period.id === selected.id) && (
          <div className="market-explorer__pinned-period">
            <small>
              {filtered.some(period => period.id === selected.id)
                ? 'Selected period · outside shown results'
                : 'Selected period · outside current filters'}
            </small>
            <Button
              type="link"
              onClick={() => onSelect(selected)}
              disabled={editing}
            >
              {selected.title}
            </Button>
            <span>{periodDates(selected.start_date, selected.end_date)}</span>
          </div>
        )}
      {!filtered.length && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            periods.length
              ? 'No periods match these filters.'
              : 'No notable periods were detected in this range. Try a longer history or another instrument.'
          }
        />
      )}
      <ol className="market-explorer__period-list">
        {filtered.slice(0, limit).map(period => {
          const sessions = sessionCount(period);
          const rank = periods.findIndex(item => item.id === period.id) + 1;
          return (
            <li key={period.id}>
              <button
                type="button"
                style={{ borderLeft: `3px solid ${kindColor(period.kind)}` }}
                aria-pressed={selected?.id === period.id}
                disabled={editing}
                className={`market-explorer__period-card ${selected?.id === period.id ? 'is-selected' : ''}`}
                onClick={() => onSelect(period)}
              >
                <span className="market-explorer__period-top">
                  <span className="market-explorer__period-number">{rank}</span>
                  <span style={{ color: kindColor(period.kind) }}>
                    {readable(period.kind)}
                  </span>
                  <span
                    className={
                      (period.return_pct || 0) < 0
                        ? 'is-negative'
                        : 'is-positive'
                    }
                  >
                    {percent(period.return_pct)}
                  </span>
                </span>
                <strong>{period.title}</strong>
                <span className="market-explorer__period-dates">
                  {periodDates(period.start_date, period.end_date)}
                </span>
                <span className="market-explorer__period-duration">
                  {sessions !== undefined
                    ? `${sessions.toLocaleString()} trading ${sessions === 1 ? 'session' : 'sessions'}`
                    : 'Session count unavailable'}
                  {period.horizon ? ` · ${label(period.horizon)}` : ''}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
      {filtered.length > limit && (
        <Button type="link" onClick={() => onLimit(limit + 15)}>
          Show {Math.min(15, filtered.length - limit)} more periods
        </Button>
      )}
      {limit > 10 && (
        <Button type="link" onClick={() => onLimit(10)}>
          Show fewer
        </Button>
      )}
    </section>
  );
};

export default PeriodList;
